/**
 * Asset conversion utility for ceSDK.
 * Converts between CollectionFile (backend) and AssetDefinition (ceSDK) formats.
 */

import { CollectionFile } from "../shared/types/files";
import { AssetDefinition } from "./cesdk-upload-handler";

// Audio placeholder thumbnail - simple waveform icon as SVG data URI
// This provides a visual indicator for audio files instead of a broken image
const AUDIO_PLACEHOLDER_THUMBNAIL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='%236b7280'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Crect x='15' y='35' width='6' height='30' rx='3'/%3E%3Crect x='27' y='25' width='6' height='50' rx='3'/%3E%3Crect x='39' y='40' width='6' height='20' rx='3'/%3E%3Crect x='51' y='20' width='6' height='60' rx='3'/%3E%3Crect x='63' y='30' width='6' height='40' rx='3'/%3E%3Crect x='75' y='38' width='6' height='24' rx='3'/%3E%3C/svg%3E";

/**
 * Convert a CollectionFile from the backend to an AssetDefinition for ceSDK.
 * @param collectionFile - The collection file from the backend
 * @returns AssetDefinition for ceSDK to use
 */
export function collectionFileToAssetDefinition(
  collectionFile: CollectionFile
): AssetDefinition {
  const isVideo = collectionFile.meta?.isVideo ?? false;
  const isImage = collectionFile.meta?.isImage ?? false;
  const isAudio = collectionFile.meta?.isAudio ?? false;

  // Determine main URL - prefer downloadUrl, fallback to storage.url
  const mainUrl =
    collectionFile.downloadUrl || collectionFile.storage?.url || "";

  // Build asset definition
  // ceSDK expects blockType, kind, fillType, shapeType and other properties in meta for proper asset handling
  // Based on ceSDK documentation: https://img.ly/docs/cesdk/js/import-media/from-remote-source/unsplash-8f31f0/
  const kind = isVideo
    ? "video"
    : isAudio
      ? "audio"
      : isImage
        ? "image"
        : "image";

  // Determine thumbnail - use placeholder for audio files
  const thumbUri = isAudio
    ? AUDIO_PLACEHOLDER_THUMBNAIL
    : collectionFile.thumbnail || undefined;

  const meta: AssetDefinition["meta"] = {
    uri: mainUrl,
    kind: kind,
    ...(thumbUri && { thumbUri }),
    ...(collectionFile.meta?.width && { width: collectionFile.meta.width }),
    ...(collectionFile.meta?.height && { height: collectionFile.meta.height }),
    ...(collectionFile.meta?.mime && { mimeType: collectionFile.meta.mime }),
  };

  // Set blockType and additional properties based on asset type
  // Video and image use the unified graphic block structure (ceSDK v1.19+)
  // with fillType to specify the content type
  if (isVideo) {
    meta.blockType = "//ly.img.ubq/graphic";
    meta.fillType = "//ly.img.ubq/fill/video";
    meta.shapeType = "//ly.img.ubq/shape/rect";
  } else if (isAudio) {
    meta.blockType = "//ly.img.ubq/audio";
  } else if (isImage) {
    // For images, use graphic blockType with fillType and shapeType (per ceSDK docs)
    meta.blockType = "//ly.img.ubq/graphic";
    meta.fillType = "//ly.img.ubq/fill/image";
    meta.shapeType = "//ly.img.ubq/shape/rect";
  } else {
    // Default to image-like properties for unknown types
    meta.blockType = "//ly.img.ubq/graphic";
    meta.fillType = "//ly.img.ubq/fill/image";
    meta.shapeType = "//ly.img.ubq/shape/rect";
  }

  // Determine tags based on file type (always set to ensure array exists)
  let tags: string[] = ["upload"];
  if (isVideo) {
    tags = ["video", "upload"];
  } else if (isAudio) {
    tags = ["audio", "upload"];
  } else if (isImage) {
    tags = ["image", "upload"];
  }

  const assetDefinition: AssetDefinition = {
    id: collectionFile.asset_id || collectionFile._id,
    label: collectionFile.name,
    meta: meta,
    tags: tags,
  };

  return assetDefinition;
}
